import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MerchSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending_timeout | error
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await fetch(`${API}/api/merch/orders/status/${sessionId}`);
        const data = await res.json();
        if (!res.ok) throw new Error();
        setOrder(data);
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (data.payment_status === 'expired') { setState('error'); return; }
      } catch (e) {
        if (attempts.current >= 3) { setState('error'); return; }
      }
      if (attempts.current >= 8) { setState('pending_timeout'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="pt-40 pb-20">
      <div className="max-w-lg mx-auto px-4 text-center">
        <div className="bg-white rounded-2xl shadow-xl p-10" data-testid="merch-success-card">
          {state === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#FF3B30] mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>Confirming your payment…</h2>
              <p className="text-neutral-500">Hang tight, this only takes a few seconds.</p>
            </>
          )}
          {state === 'paid' && order && (
            <>
              <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>Order Confirmed! 🎉</h2>
              <p className="text-neutral-600 mb-6">
                {order.quantity}× {order.product_name}{order.size ? ` (${order.size})` : ''} — ${order.amount.toFixed(2)} CAD
              </p>
              <p className="text-sm text-neutral-500 mb-8">A receipt has been emailed to you by Stripe. We'll ship your gear soon!</p>
              <Link to="/merch"><Button data-testid="back-to-merch-btn" className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl h-12 px-8">Keep Shopping</Button></Link>
            </>
          )}
          {state === 'pending_timeout' && (
            <>
              <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>Payment still processing</h2>
              <p className="text-neutral-500 mb-6">Your payment is taking longer than usual. Check your email for the Stripe receipt — your order is safe.</p>
              <Link to="/merch"><Button variant="outline" className="rounded-xl h-11 px-6">Back to Merch</Button></Link>
            </>
          )}
          {state === 'error' && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>Something went wrong</h2>
              <p className="text-neutral-500 mb-6">We couldn't confirm this order. If you were charged, contact us and we'll sort it out.</p>
              <Link to="/merch"><Button variant="outline" className="rounded-xl h-11 px-6">Back to Merch</Button></Link>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
